import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { getInvitationByToken, acceptInvitation } from '../lib/api'
import type { InvitationPublic } from '../lib/api'
import { getErrorMessage } from '../lib/errors'
import { LoadingSpinner } from '../components/ui/LoadingSpinner'

type PageState =
  | { status: 'loading' }
  | { status: 'loaded'; invitation: InvitationPublic }
  | { status: 'error'; message: string }
  | { status: 'accepted' }

type AuthMode = 'login' | 'signup'

function formatExpiry(expiresAt: string): string {
  return new Date(expiresAt).toLocaleDateString('es-MX', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

export function InvitationPage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { user, signIn, signUp, refreshProfile } = useAuth()

  const [pageState, setPageState] = useState<PageState>({ status: 'loading' })
  const [authMode, setAuthMode] = useState<AuthMode>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [authError, setAuthError] = useState<string | null>(null)
  const [authLoading, setAuthLoading] = useState(false)
  const [acceptLoading, setAcceptLoading] = useState(false)

  useEffect(() => {
    if (!token) {
      setPageState({ status: 'error', message: 'Token de invitación inválido.' })
      return
    }
    getInvitationByToken(token)
      .then((inv) => setPageState({ status: 'loaded', invitation: inv }))
      .catch((err) =>
        setPageState({ status: 'error', message: getErrorMessage(err) })
      )
  }, [token])

  const handleAccept = async () => {
    if (!token) return
    setAcceptLoading(true)
    try {
      await acceptInvitation(token)
      await refreshProfile()
      setPageState({ status: 'accepted' })
      setTimeout(() => navigate('/dashboard', { replace: true }), 1500)
    } catch (err) {
      setPageState({ status: 'error', message: getErrorMessage(err) })
    } finally {
      setAcceptLoading(false)
    }
  }

  const handleAuthAndAccept = async (e: React.FormEvent) => {
    e.preventDefault()
    setAuthError(null)
    setAuthLoading(true)
    try {
      if (authMode === 'login') {
        await signIn(email, password)
      } else {
        await signUp(email, password, fullName)
      }
      // After sign in/up, accept the invitation
      await handleAccept()
    } catch (err) {
      setAuthError(getErrorMessage(err))
    } finally {
      setAuthLoading(false)
    }
  }

  if (pageState.status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (pageState.status === 'accepted') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 text-3xl mb-4">
            &#10003;
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Te uniste a la organización</h1>
          <p className="text-slate-500 mt-2">Redirigiendo al dashboard...</p>
        </div>
      </div>
    )
  }

  if (pageState.status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="w-full max-w-md text-center">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
            <h1 className="text-xl font-bold text-slate-900 mb-2">Invitación no válida</h1>
            <p className="text-slate-500 text-sm">{pageState.message}</p>
            <button
              className="btn btn-primary mt-6"
              onClick={() => navigate('/login', { replace: true })}
            >
              Ir al login
            </button>
          </div>
        </div>
      </div>
    )
  }

  const { invitation } = pageState

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-emerald-600 text-white font-bold text-xl mb-4">
            S
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Invitación de equipo</h1>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
          <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 mb-6 text-sm">
            <p className="text-slate-600">
              Has sido invitado a unirte a{' '}
              <span className="font-semibold text-slate-900">{invitation.organization_name}</span>{' '}
              como <span className="font-medium capitalize">{invitation.role === 'admin' ? 'Administrador' : 'Editor'}</span>.
            </p>
            <p className="text-slate-400 mt-1">Invitación válida hasta {formatExpiry(invitation.expires_at)}</p>
          </div>

          {user ? (
            // User already authenticated — show join button
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Sesión activa como <span className="font-medium">{user.email}</span>.
              </p>
              <button
                className="btn btn-primary w-full"
                onClick={handleAccept}
                disabled={acceptLoading}
              >
                {acceptLoading ? 'Uniéndose...' : `Unirse a ${invitation.organization_name}`}
              </button>
            </div>
          ) : (
            // Not authenticated — show auth form
            <div className="space-y-4">
              <div className="flex rounded-lg border border-slate-200 overflow-hidden text-sm">
                <button
                  type="button"
                  className={`flex-1 py-2 font-medium transition-colors ${
                    authMode === 'login'
                      ? 'bg-slate-900 text-white'
                      : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                  onClick={() => setAuthMode('login')}
                >
                  Iniciar sesión
                </button>
                <button
                  type="button"
                  className={`flex-1 py-2 font-medium transition-colors ${
                    authMode === 'signup'
                      ? 'bg-slate-900 text-white'
                      : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                  onClick={() => setAuthMode('signup')}
                >
                  Crear cuenta
                </button>
              </div>

              <form onSubmit={handleAuthAndAccept} className="space-y-3">
                {authMode === 'signup' && (
                  <div>
                    <label className="label" htmlFor="full-name">Nombre completo</label>
                    <input
                      id="full-name"
                      type="text"
                      className="input w-full"
                      placeholder="Tu nombre"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      disabled={authLoading}
                      required
                    />
                  </div>
                )}

                <div>
                  <label className="label" htmlFor="inv-email">Correo</label>
                  <input
                    id="inv-email"
                    type="email"
                    className="input w-full"
                    placeholder={invitation.email}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={authLoading}
                    required
                  />
                </div>

                <div>
                  <label className="label" htmlFor="inv-password">Contraseña</label>
                  <input
                    id="inv-password"
                    type="password"
                    className="input w-full"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={authLoading}
                    required
                  />
                </div>

                {authError && (
                  <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                    {authError}
                  </div>
                )}

                <button
                  type="submit"
                  className="btn btn-primary w-full"
                  disabled={authLoading}
                >
                  {authLoading
                    ? 'Procesando...'
                    : authMode === 'login'
                    ? `Iniciar sesión y unirse`
                    : `Crear cuenta y unirse`}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
